import { useMemo } from 'react';
import classNames from 'classnames';

import { FinancialProductType, LeaselinkOffer } from 'types/models/LeaselinkOffer';
import AmountFormatter from '@misc/AmountFormatter';
import Button from '@components/atoms/Button';
import Typography from '@components/atoms/Typography';
import MessageWithBold from '@components/atoms/MessageWithBold';
import { FormattedMessage, MessageDescriptor } from 'utils/intl';

import LeaselinkOptions from './LeaselinkOptions';
import messages from './LeaselinkCalculator.messages';
import classes from './LeaselinkCalculator.module.scss';

const typeMessagesMap: Record<FinancialProductType, MessageDescriptor> = {
  DeferredPayment: messages.deferred,
  Loan: messages.loan,
  OperationalLeasing: messages.leasing,
};

type Props = {
  offers: LeaselinkOffer[];
  orientation?: 'vertical' | 'horizontal';
  activeLeasingId: string | undefined;
  showSingleFinancialType?: boolean;
  setActiveLeasingOffer: (value: LeaselinkOffer | undefined) => void;
};

const LeaselinkCalculator = ({
  offers,
  orientation = 'vertical',
  activeLeasingId,
  showSingleFinancialType,
  setActiveLeasingOffer,
}: Props) => {
  const allFinancialProductTypes: FinancialProductType[] = ['OperationalLeasing', 'Loan', 'DeferredPayment'];

  // todo after update typescript version change to groupBy
  const offersPerType = useMemo(
    () => ({
      OperationalLeasing: offers.filter((offer) => offer.FinancialProductType === 'OperationalLeasing'),
      Loan: offers.filter((offer) => offer.FinancialProductType === 'Loan'),
      DeferredPayment: offers.filter((offer) => offer.FinancialProductType === 'DeferredPayment'),
    }),
    [offers] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const activeOffer = offers.find((offer) => offer.CalculationPackageId === activeLeasingId);

  const filteredFinancialTypes = allFinancialProductTypes.filter(
    (financialType) => offersPerType[financialType].length
  );

  const setActiveLeasingId = (leasingId: string) => {
    setActiveLeasingOffer(offers.find((offer) => offer.CalculationPackageId === leasingId));
  };

  return (
    !!activeOffer && (
      <div className={classNames(classes.container, { [classes.horizontal]: orientation === 'horizontal' })}>
        {(filteredFinancialTypes.length > 1 || showSingleFinancialType) && (
          <div className={classes.buttons}>
            {filteredFinancialTypes.map((financialType) => (
              <Button
                key={financialType}
                color="secondary"
                variant="outlined"
                className={classNames(classes.button, {
                  [classes.active]: activeOffer.FinancialProductType === financialType,
                })}
                onClick={() => {
                  setActiveLeasingId(offersPerType[financialType][0].CalculationPackageId);
                }}
                wide
              >
                <FormattedMessage {...typeMessagesMap[financialType]} />
              </Button>
            ))}
          </div>
        )}

        <div className={classes.selectorWrapper}>
          <div>
            <Typography variant="body1" renderAs="div" className={classes.selectorLabel}>
              <FormattedMessage {...messages.numberOfMonths} />
            </Typography>
            <LeaselinkOptions
              offersPerType={offersPerType}
              activeOffer={activeOffer}
              type="NumberOfInstallments"
              onClick={setActiveLeasingId}
            />
          </div>
          <div>
            <Typography variant="body1" renderAs="div" className={classes.selectorLabel}>
              <FormattedMessage {...messages.initialFee} />
            </Typography>
            <LeaselinkOptions
              offersPerType={offersPerType}
              activeOffer={activeOffer}
              type="PercentInitialFee"
              onClick={setActiveLeasingId}
            />
          </div>
          {activeOffer.FinancialProductType !== 'Loan' && (
            <div>
              <Typography variant="body1" renderAs="div" className={classes.selectorLabel}>
                <FormattedMessage {...messages.residualFee} />
              </Typography>
              <LeaselinkOptions
                offersPerType={offersPerType}
                activeOffer={activeOffer}
                type="PercentResidualFee"
                onClick={setActiveLeasingId}
              />
            </div>
          )}
        </div>
        <div className={classes.column}>
          <div className={classes.pricesWrapper}>
            <div className={classes.prices}>
              <Typography variant="body2" renderAs="div" className={classes.price}>
                <MessageWithBold
                  message={messages.initialFeePrice}
                  values={{
                    price: (
                      <AmountFormatter
                        price={{ amount: activeOffer.GrossInitialFee * 100, currency: 'PLN' }}
                        type="gross"
                      />
                    ),
                  }}
                />
              </Typography>
              <Typography variant="body2" renderAs="div" className={classes.price}>
                <MessageWithBold
                  message={messages.residualFeePrice}
                  values={{
                    price: (
                      <AmountFormatter
                        price={{ amount: activeOffer.GrossResidualFee * 100, currency: 'PLN' }}
                        type="gross"
                      />
                    ),
                  }}
                />
              </Typography>
            </div>
            <div className={classes.monthlyPriceWrapper}>
              <Typography variant="body2" renderAs="div" className={classes.price}>
                <FormattedMessage {...messages.monthlyInstallment} />
              </Typography>
              <Typography variant="h2" weight="700" renderAs="div" className={classes.monthlyPrice}>
                <AmountFormatter
                  price={{ amount: activeOffer.GrossMonthlyInstallment * 100, currency: 'PLN' }}
                  type="gross"
                />
              </Typography>
            </div>
          </div>
          <Typography variant="body2" renderAs="div" className={classes.info}>
            <FormattedMessage {...messages.info} />
          </Typography>
        </div>
      </div>
    )
  );
};

export default LeaselinkCalculator;
